import { useEffect, useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { MessageSquare, ArrowLeft, Loader2, Phone, Video, Trash2 } from 'lucide-react';
import { messagesApi } from '@/api/messages';
import ClearDmChatDialog from './ClearDmChatDialog';
import { useChatStore } from '@/store/chatStore';
import { useAuthStore } from '@/store/authStore';
import { useCallStore } from '@/store/callStore';
import { useMobile } from '@/pages/chat/ChatPage';
import { usersApi } from '@/api/users';
import { DirectMessage, Message } from '@/types';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import BulkActionBar from './BulkActionBar';
import OutboxBanner from './OutboxBanner';
import Avatar from '@/components/ui/Avatar';
import { useSelectionStore } from '@/store/selectionStore';
import NickName from '@/components/ui/NickName';

function dmToMessage(dm: DirectMessage): Message {
  return {
    id: dm.id,
    channelId: '',
    authorId: dm.senderId,
    content: dm.content,
    type: dm.type,
    fileUrl: dm.fileUrl,
    fileName: null,
    fileSize: null,
    mediaFileId: dm.mediaFileId,
    editedAt: dm.editedAt,
    deletedAt: null,
    // Phase 8.1 — пробрасываем DM-ответ через общий Message-shape, чтобы
    // MessageItem мог рендерить превью и поддерживать «прыжок» по клику.
    replyToId: dm.replyToDmId ?? null,
    replyTo: dm.replyToDm
      ? {
          id: dm.replyToDm.id,
          content: dm.replyToDm.content,
          author: {
            id: dm.replyToDm.sender.id,
            username: dm.replyToDm.sender.username,
            displayName: dm.replyToDm.sender.displayName,
          },
        }
      : null,
    createdAt: dm.createdAt,
    author: dm.sender,
    readAt: dm.readAt,
    // Phase 6 — bots: пробрасываем inline-клавиатуру в общий Message-shape,
    // чтобы MessageItem отрисовал кнопки и в DM (как в каналах).
    botKeyboard: dm.botKeyboard ?? null,
  };
}

export default function DMView() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { setShowSidebar } = useMobile();
  const { setActiveDMUser, setDMMessages, dmMessages, dmTyping } = useChatStore();
  const startCall = useCallStore((s) => s.startCall);
  const callPhase = useCallStore((s) => s.phase);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  useEffect(() => {
    if (userId) setActiveDMUser(userId);
    useSelectionStore.getState().exit();
    return () => setActiveDMUser(null);
  }, [userId, setActiveDMUser]);

  const { data: partner, isLoading: partnerLoading, isError: partnerError } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersApi.getProfile(userId!),
    enabled: !!userId,
    retry: 1,
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: msgsLoading } = useInfiniteQuery({
    queryKey: ['messages', 'dm', userId],
    queryFn: ({ pageParam }) =>
      messagesApi.getDMs(userId!, { cursor: pageParam as string, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!userId && !!partner,
  });

  useEffect(() => {
    if (!data || !userId) return;
    const allMsgs = data.pages.flatMap((p) => p.data).reverse();
    setDMMessages(userId, allMsgs);
  }, [data, userId, setDMMessages]);

  const rawDMs = userId ? (dmMessages[userId] ?? []) : [];
  const messages = useMemo(() => rawDMs.map(dmToMessage), [rawDMs]);
  const isTyping = userId ? (dmTyping[userId] ?? false) : false;

  // First unread DM = first incoming message still with readAt === null.
  // Server marks everything read on getDMs fetch, but the response returned
  // BEFORE the updateMany still carries the old readAt, so we can use it.
  const myId = useAuthStore((s) => s.user?.id);
  const firstUnreadId = useMemo(() => {
    if (!myId) return null;
    for (const dm of rawDMs) {
      if (dm.senderId !== myId && dm.readAt === null) return dm.id;
    }
    return null;
  }, [rawDMs, myId]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!userId) {
    return (
      <div className="flex-1 flex items-center justify-center text-surface-500">
        <div className="text-center">
          <MessageSquare size={48} className="mx-auto mb-3 opacity-30" />
          <p>Выберите диалог</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (partnerLoading || msgsLoading) {
    return (
      <div className="flex flex-col flex-1 min-w-0">
        <div className="h-14 border-b border-surface-800 px-3 flex items-center gap-3 flex-shrink-0 bg-surface-900">
          <button
            onClick={() => { setShowSidebar(true); navigate('/'); }}
            className="md:hidden btn-ghost p-1.5 text-surface-400 -ml-1"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin text-surface-500" />
            <span className="text-surface-500 text-sm">Загрузка...</span>
          </div>
        </div>
        <div className="flex-1" />
      </div>
    );
  }

  // Error state
  if (partnerError) {
    return (
      <div className="flex flex-col flex-1 min-w-0">
        <div className="h-14 border-b border-surface-800 px-3 flex items-center gap-3 flex-shrink-0 bg-surface-900">
          <button
            onClick={() => { setShowSidebar(true); navigate('/'); }}
            className="md:hidden btn-ghost p-1.5 text-surface-400 -ml-1"
          >
            <ArrowLeft size={20} />
          </button>
          <span className="text-surface-400 text-sm">Диалог</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-surface-400 mb-2">Пользователь не найден</p>
            <button
              onClick={() => { setShowSidebar(true); navigate('/'); }}
              className="text-brand-400 text-sm hover:underline"
            >
              Назад
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-w-0">
      {/* Header */}
      <div className="h-14 border-b border-surface-800 px-3 flex items-center gap-3 flex-shrink-0 bg-surface-900">
        <button
          onClick={() => { setShowSidebar(true); navigate('/'); }}
          className="md:hidden btn-ghost p-1.5 text-surface-400 -ml-1"
          title="Назад"
        >
          <ArrowLeft size={20} />
        </button>
        {partner && (
          <button
            type="button"
            onClick={() => navigate(`/u/${partner.username}`)}
            className="flex items-center gap-3 min-w-0 flex-1 text-left hover:opacity-80 transition-opacity"
            title="Профиль"
          >
            <Avatar src={partner.avatarUrl} username={partner.username} size="sm" status={partner.status} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-surface-100 text-sm leading-tight truncate">
                <NickName user={partner ?? {}} fallback={userId} />
              </p>
              <p className="text-xs leading-tight">
                {isTyping ? (
                  <span className="text-brand-400">печатает...</span>
                ) : (
                  <span className="text-surface-500 font-mono">@{partner.username}</span>
                )}
              </p>
            </div>
          </button>
        )}

        {/* ── Call buttons (voice / video) ─────────────────────────────── */}
        {partner && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              disabled={callPhase !== 'idle'}
              onClick={() => startCall(partner.id, 'VOICE', partner.displayName)}
              className="btn-ghost p-2 text-surface-300 hover:text-brand-300 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Голосовой звонок"
            >
              <Phone size={18} />
            </button>
            <button
              type="button"
              disabled={callPhase !== 'idle'}
              onClick={() => startCall(partner.id, 'VIDEO', partner.displayName)}
              className="btn-ghost p-2 text-surface-300 hover:text-brand-300 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Видеозвонок"
            >
              <Video size={18} />
            </button>
            {/* Phase 16 — очистить переписку с собеседником. Открывает
                модалку с выбором «У меня / У всех». */}
            <button
              type="button"
              onClick={() => setClearDialogOpen(true)}
              className="btn-ghost p-2 text-surface-300 hover:text-red-400"
              title="Очистить переписку"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
      </div>
      {partner && (
        <ClearDmChatDialog
          open={clearDialogOpen}
          onClose={() => setClearDialogOpen(false)}
          peerId={partner.id}
          peerDisplayName={partner.displayName}
        />
      )}

      <OutboxBanner />
      <BulkActionBar canDelete={true} onChanged={() => { /* DM list re-fetched on socket */ }} />

      <MessageList
        key={userId}
        messages={messages}
        onLoadMore={handleLoadMore}
        hasMore={hasNextPage}
        loading={isFetchingNextPage}
        onReply={setReplyTo}
        firstUnreadId={firstUnreadId}
        selectionScope={userId ? { kind: 'dm', userId } : undefined}
      />

      <MessageInput
        recipientId={userId}
        placeholder={`Написать ${partner?.displayName ?? ''}...`}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
      />
    </div>
  );
}
